import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/**
 * UserMenu — dropdown in the nav header for authenticated users.
 * Shows avatar, display name, and dropdown with Account/Admin/Sign Out.
 *
 * Renders the "Sign In" NavLink when no user is authenticated, so it's a
 * safe drop-in replacement for hardcoded "Sign In" links in any header.
 */
export default function UserMenu() {
  const { user, isAuthenticated, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  if (!isAuthenticated || !user) {
    return (
      <NavLink to="/scheduling" className="user-menu__signin">
        Sign In
      </NavLink>
    );
  }

  const displayLabel = user.display_name || user.email;
  const avatarLetter = (displayLabel || "?")[0].toUpperCase();
  const hasAvatar = !!user.avatar_url;

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/");
  };

  return (
    <div className="user-menu" ref={menuRef}>
      <button
        className="user-menu__trigger"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="user-menu__avatar">
          {hasAvatar ? (
            <img src={user.avatar_url} alt="" referrerPolicy="no-referrer" />
          ) : (
            avatarLetter
          )}
        </span>
        <span className="user-menu__name">{displayLabel}</span>
      </button>

      {open && (
        <div className="user-menu__dropdown">
          <button
            className="user-menu__item"
            onClick={() => { setOpen(false); navigate("/account"); }}
          >
            Account
          </button>
          {user.role === "admin" && (
            <button
              className="user-menu__item"
              onClick={() => { setOpen(false); navigate("/admin"); }}
            >
              Admin
            </button>
          )}
          <button
            className="user-menu__item user-menu__item--danger"
            onClick={handleLogout}
          >
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}
